/**
 * Development mode canvas: Solution → Deliverable → PRD
 *
 * Deliverable = minimal indivisible set of solutions that creates stakeholder value.
 * PRD = set of interrelated deliverables that create synergies when delivered together.
 *
 * Single-deliverable collapse: when a PRD has exactly one deliverable, the
 * deliverable node is hidden and solutions connect directly to the PRD.
 */
import { useCallback, useState, useMemo, useEffect, useRef } from 'react';
import ReactFlow, {
  ReactFlowProvider,
  useNodesState,
  useEdgesState,
  useNodesInitialized,
  Handle,
  Position,
  type Node,
  type Edge,
} from 'reactflow';
import 'reactflow/dist/style.css';
import dagre from '@dagrejs/dagre';
import { CT } from '../lib/theme';

const COLORS: Record<string, { bg: string; border: string }> = {
  solution: { bg: CT.darkBg, border: CT.green },
  deliverable: { bg: CT.darkBg, border: CT.orange },
  prd: { bg: CT.darkBg, border: CT.primaryBlue },
};

const HANDLE_SIZE = 7;
const HANDLE_SIZE_ACTIVE = 12;

function PRDNode({
  id,
  data,
}: {
  id: string;
  data: {
    type: string;
    label: string;
    description?: string;
    jiraKey?: string;
    _expanded?: boolean;
    _targetHandleActive?: boolean;
    _sourceHandleActive?: boolean;
    _deadEnd?: boolean;
    onHandleClick?: (nodeId: string, handleType: string) => void;
  };
}) {
  const c = COLORS[data.type] ?? { bg: CT.darkBg, border: CT.lightGrey };
  const expanded = !!data._expanded;
  const hasDesc = data.description && data.description.length > 0;
  const targetActive = !!data._targetHandleActive;
  const sourceActive = !!data._sourceHandleActive;
  const onHandleClick = data.onHandleClick;

  const deadEnd = !!data._deadEnd;
  const isLeftmost = data.type === 'solution';
  const isRightmost = data.type === 'prd';

  const handleStyle = (active: boolean) => ({
    background: c.border,
    border: 'none',
    width: active ? HANDLE_SIZE_ACTIVE : HANDLE_SIZE,
    height: active ? HANDLE_SIZE_ACTIVE : HANDLE_SIZE,
    transition: 'width 0.2s ease, height 0.2s ease',
  });

  const wrapHandle = (type: 'target' | 'source', active: boolean) => (
    <div
      onClick={(e) => {
        e.stopPropagation();
        onHandleClick?.(id, type);
      }}
      style={{
        position: 'absolute',
        top: '50%',
        [type === 'target' ? 'left' : 'right']: 0,
        transform: 'translateY(-50%)',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: active ? HANDLE_SIZE_ACTIVE + 8 : HANDLE_SIZE + 8,
        height: active ? HANDLE_SIZE_ACTIVE + 8 : HANDLE_SIZE + 8,
        marginLeft: type === 'target' ? -4 : 0,
        marginRight: type === 'source' ? -4 : 0,
      }}
    >
      <Handle
        type={type}
        position={type === 'target' ? Position.Left : Position.Right}
        style={handleStyle(active)}
      />
    </div>
  );

  return (
    <div
      style={{
        background: c.bg,
        border: `1.5px solid ${c.border}`,
        borderRadius: 8,
        padding: '8px 12px',
        width: expanded ? 'auto' : 160,
        minWidth: 160,
        maxWidth: expanded ? 320 : 160,
        cursor: hasDesc ? 'pointer' : 'default',
        fontFamily: CT.font,
        letterSpacing: '0em',
        position: 'relative',
        transition: 'opacity 0.25s ease',
      }}
    >
      {!isLeftmost && wrapHandle('target', targetActive)}
      {!isRightmost && !deadEnd && wrapHandle('source', sourceActive)}

      <div
        style={{
          color: CT.white,
          fontSize: 12,
          fontWeight: 500,
          lineHeight: 1.3,
          letterSpacing: '0.01em',
          wordBreak: 'break-word',
          marginBottom: expanded && hasDesc ? 8 : 0,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 6,
        }}
      >
        <span style={{ flex: 1 }}>{data.label}</span>
        {deadEnd && (
          <span
            title="Not assigned to a deliverable"
            style={{
              flexShrink: 0,
              fontSize: 9,
              lineHeight: '16px',
              padding: '0 5px',
              borderRadius: 4,
              background: `${CT.orange}20`,
              color: CT.orange,
              fontWeight: 600,
              letterSpacing: '0.03em',
              whiteSpace: 'nowrap',
            }}
          >
            ✧ TODO
          </span>
        )}
        {data.jiraKey && (
          <span
            style={{
              flexShrink: 0,
              fontSize: 9,
              lineHeight: '16px',
              padding: '0 5px',
              borderRadius: 4,
              background: `${CT.primaryBlue}20`,
              color: CT.primaryBlue,
              fontWeight: 600,
              letterSpacing: '0.03em',
              whiteSpace: 'nowrap',
            }}
          >
            {data.jiraKey}
          </span>
        )}
      </div>

      {expanded && hasDesc && (
        <div
          style={{
            color: CT.paleGrey,
            fontSize: 10.5,
            fontWeight: 200,
            lineHeight: 1.4,
            letterSpacing: '0.01em',
            borderTop: `1px solid ${c.border}30`,
            paddingTop: 6,
            marginTop: 2,
          }}
        >
          {data.description}
        </div>
      )}
    </div>
  );
}

const nodeTypes = { prd: PRDNode };

function edgeColorForTarget(targetId: string, nodeMap: Record<string, Node>) {
  const node = nodeMap[targetId];
  if (!node) return CT.lightGrey;
  return COLORS[node.data?.type as string]?.border ?? CT.lightGrey;
}

function buildEdge(src: string, tgt: string, color: string): Edge {
  return {
    id: `${src}-${tgt}`,
    source: src,
    target: tgt,
    type: 'smoothstep',
    style: { stroke: color, strokeWidth: 1.5, opacity: 0.5 },
    animated: false,
  };
}

function collectDownstream(
  edgeId: string,
  edgeList: Edge[],
  edgeIndex: Record<string, Edge>
) {
  const edge = edgeIndex[edgeId];
  if (!edge) return { nodeIds: new Set<string>(), edgeIds: new Set<string>() };

  const nodeIds = new Set<string>([edge.source, edge.target]);
  const edgeIds = new Set<string>([edgeId]);

  const queue = [edge.target];
  while (queue.length > 0) {
    const current = queue.shift()!;
    for (const e of edgeList) {
      if (e.source === current && !edgeIds.has(e.id)) {
        edgeIds.add(e.id);
        nodeIds.add(e.target);
        queue.push(e.target);
      }
    }
  }

  const upQueue = [edge.source];
  while (upQueue.length > 0) {
    const current = upQueue.shift()!;
    for (const e of edgeList) {
      if (e.target === current && !edgeIds.has(e.id)) {
        edgeIds.add(e.id);
        nodeIds.add(e.source);
        upQueue.push(e.source);
      }
    }
  }

  return { nodeIds, edgeIds };
}

const DEFAULT_W = 160;
const DEFAULT_H = 52;

function dagreLayout(rawNodes: Node[], rawEdges: Array<[string, string]>, measured?: Record<string, { w: number; h: number }>): Node[] {
  const g = new dagre.graphlib.Graph();
  g.setGraph({ rankdir: 'LR', nodesep: 30, ranksep: 80, marginx: 20, marginy: 20 });
  g.setDefaultEdgeLabel(() => ({}));

  for (const n of rawNodes) {
    const m = measured?.[n.id];
    g.setNode(n.id, { width: m?.w ?? DEFAULT_W, height: m?.h ?? DEFAULT_H });
  }

  const nodeIdSet = new Set(rawNodes.map((n) => n.id));
  for (const [src, tgt] of rawEdges) {
    if (nodeIdSet.has(src) && nodeIdSet.has(tgt)) g.setEdge(src, tgt);
  }

  dagre.layout(g);

  return rawNodes.map((n) => {
    const gn = g.node(n.id);
    const m = measured?.[n.id];
    return {
      ...n,
      position: { x: gn.x - gn.width / 2, y: gn.y - gn.height / 2 },
      ...(m ? { width: m.w, height: m.h } : {}),
    };
  });
}

export type PRDProjectData = {
  nodes: Array<{
    id: string;
    type: string;
    data: { type: string; id: number; label: string; description: string; jiraKey?: string };
  }>;
  edges: Array<[string, string]>;
};

const EMPTY_NODES: PRDProjectData['nodes'] = [];
const EMPTY_EDGES: PRDProjectData['edges'] = [];

function PRDProjectInner({
  data,
}: {
  data: PRDProjectData | null;
}) {
  const rawNodes = data?.nodes ?? EMPTY_NODES;
  const edgePairs = data?.edges ?? EMPTY_EDGES;

  const nodeMap = useMemo(
    () => Object.fromEntries(rawNodes.map((n) => [n.id, n])),
    [rawNodes]
  );

  const baseEdges = useMemo(() => {
    if (edgePairs.length === 0) return [];
    return edgePairs.map(([src, tgt]) =>
      buildEdge(src, tgt, edgeColorForTarget(tgt, nodeMap as Record<string, Node>))
    );
  }, [edgePairs, nodeMap]);

  const edgeIndex = useMemo(
    () => Object.fromEntries(baseEdges.map((e) => [e.id, e])),
    [baseEdges]
  );

  const initialLayout = useMemo(
    () => dagreLayout(rawNodes as Node[], edgePairs),
    [rawNodes, edgePairs]
  );

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [, , onEdgesChange] = useEdgesState([]);
  const nodesInitialized = useNodesInitialized();
  const didMeasureRef = useRef(false);
  const nodesRef = useRef(nodes);
  nodesRef.current = nodes;
  const [handleFilters, setHandleFilters] = useState<
    Array<{ nodeId: string; direction: 'in' | 'out' }>
  >([]);

  const selectedEdgeIds = useMemo(() => {
    const set = new Set<string>();
    for (const f of handleFilters) {
      for (const e of baseEdges) {
        if (f.direction === 'out' && e.source === f.nodeId) set.add(e.id);
        if (f.direction === 'in' && e.target === f.nodeId) set.add(e.id);
      }
    }
    return set;
  }, [handleFilters, baseEdges]);

  // Pass 1: initial layout with estimated dimensions
  useEffect(() => {
    didMeasureRef.current = false;
    setNodes(initialLayout);
  }, [initialLayout, setNodes]);

  // Pass 2: re-layout with actual measured dimensions
  useEffect(() => {
    if (!nodesInitialized || didMeasureRef.current) return;
    didMeasureRef.current = true;
    const measured: Record<string, { w: number; h: number }> = {};
    let hasMeasured = false;
    for (const n of nodesRef.current) {
      if (n.width && n.height) {
        measured[n.id] = { w: n.width, h: n.height };
        hasMeasured = true;
      }
    }
    if (!hasMeasured) return;
    setNodes(dagreLayout(rawNodes as Node[], edgePairs, measured));
  }, [nodesInitialized, rawNodes, edgePairs, setNodes]);

  // Reset filter when data changes
  const prevData = useRef(data);
  useEffect(() => {
    if (prevData.current !== data) {
      prevData.current = data;
      setHandleFilters([]);
    }
  }, [data]);

  const filterResult = useMemo(() => {
    if (selectedEdgeIds.size === 0) return null;
    const combinedNodes = new Set<string>();
    const combinedEdges = new Set<string>();
    for (const eid of selectedEdgeIds) {
      const { nodeIds, edgeIds } = collectDownstream(eid, baseEdges, edgeIndex);
      nodeIds.forEach((id) => combinedNodes.add(id));
      edgeIds.forEach((id) => combinedEdges.add(id));
    }
    return { nodeIds: combinedNodes, edgeIds: combinedEdges };
  }, [selectedEdgeIds, baseEdges, edgeIndex]);

  const hasFilter = filterResult !== null;

  const onHandleClick = useCallback(
    (nodeId: string, handleType: string) => {
      const direction: 'in' | 'out' = handleType === 'target' ? 'in' : 'out';
      setHandleFilters((prev) => {
        const alreadySet =
          prev.length === 1 &&
          prev[0].nodeId === nodeId &&
          prev[0].direction === direction;
        return alreadySet ? [] : [{ nodeId, direction }];
      });
    },
    []
  );

  const nodesWithOutgoing = useMemo(() => {
    const set = new Set<string>();
    for (const e of baseEdges) set.add(e.source);
    return set;
  }, [baseEdges]);

  const styledNodes = useMemo(() => {
    let base: Node[];
    if (!hasFilter) {
      base = nodes;
    } else {
      const measured: Record<string, { w: number; h: number }> = {};
      for (const n of nodes) {
        if (n.width && n.height) measured[n.id] = { w: n.width, h: n.height };
      }
      base = dagreLayout(
        (rawNodes as Node[]).filter((n) => filterResult!.nodeIds.has(n.id)),
        edgePairs.filter(([src, tgt]) => filterResult!.nodeIds.has(src) && filterResult!.nodeIds.has(tgt)),
        measured
      );
    }
    return base.map((n) => {
      const targetActive =
        hasFilter &&
        baseEdges.some((e) => e.target === n.id && selectedEdgeIds.has(e.id));
      const sourceActive =
        hasFilter &&
        baseEdges.some((e) => e.source === n.id && selectedEdgeIds.has(e.id));
      const nodeType = n.data?.type as string;
      const isDeadEnd = nodeType === 'solution' && !nodesWithOutgoing.has(n.id);
      return {
        ...n,
        zIndex: n.data?._expanded ? 1000 : 0,
        data: {
          ...n.data,
          _targetHandleActive: targetActive,
          _sourceHandleActive: sourceActive,
          _deadEnd: isDeadEnd,
          onHandleClick,
        },
      };
    });
  }, [nodes, hasFilter, filterResult, baseEdges, selectedEdgeIds, onHandleClick, nodesWithOutgoing, rawNodes, edgePairs]);

  const styledEdges = useMemo(() => {
    const base = !hasFilter
      ? baseEdges.map((e) => ({ ...e, style: { ...e.style, strokeWidth: 1.5, opacity: 0.5 } }))
      : baseEdges
          .filter((e) => filterResult!.edgeIds.has(e.id))
          .map((e) => ({
            ...e,
            style: {
              stroke: e.style?.stroke,
              strokeWidth: selectedEdgeIds.has(e.id) ? 3.5 : 1.5,
              opacity: selectedEdgeIds.has(e.id) ? 0.95 : 0.5,
            },
          }));
    return base.map((e) => ({ ...e, style: { ...e.style, pointerEvents: 'none' as const } }));
  }, [baseEdges, hasFilter, filterResult, selectedEdgeIds]);

  const onNodeClick = useCallback(
    (_event: React.MouseEvent, node: Node) => {
      if (!node.data?.description) return;
      setNodes((nds) =>
        nds.map((n) =>
          n.id === node.id
            ? { ...n, data: { ...n.data, _expanded: !n.data?._expanded } }
            : n
        )
      );
    },
    [setNodes]
  );

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        background: CT.darkGrey,
        fontFamily: CT.font,
      }}
    >
      <ReactFlow
        nodes={styledNodes}
        edges={styledEdges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={onNodeClick}
        nodeTypes={nodeTypes}
        elementsSelectable={false}
        nodesConnectable={false}
        edgesUpdatable={false}
        connectOnClick={false}
        defaultViewport={{ x: 20, y: 50, zoom: 1 }}
        minZoom={0.2}
        maxZoom={1.8}
        proOptions={{ hideAttribution: true }}
      />
    </div>
  );
}

export default function PRDProject(props: { data: PRDProjectData | null }) {
  return (
    <ReactFlowProvider>
      <PRDProjectInner {...props} />
    </ReactFlowProvider>
  );
}
